/**
 * MAHI — the client half of the discovery preference filter.
 *
 * The feed's fast path is a pre-built CDN snapshot (see prefetch.js): one
 * static, cacheable JSON object per gender, shared by every user. Preferences
 * are per-user, so they cannot be baked into it — the snapshot either gets
 * filtered here, in the browser, or the fast path is given up entirely for
 * anyone who answered the picker. This module is that filter.
 *
 * **It must agree with the server exactly.** The same rule lives in
 * services/mahi/discovery_prefs.py, which is what `GET /inventory/dresses`
 * applies. Two places, one rule — so the two things that keep them honest:
 *
 *   - The category list is never derived here. `/auth/me` hands down
 *     `discovery_taxonomy_categories`, already resolved from the shopper-facing
 *     groups by the backend, so the group → taxonomy mapping exists in exactly
 *     one place and a client one deploy behind cannot filter on a stale copy.
 *   - Price is read the same way the index reads it: `offer_price` first, then
 *     `price`, and a zero is absent rather than free. A shopper who capped
 *     themselves at ₹5,000 has not agreed to be shown things we cannot price.
 *
 * When the filter leaves too little to swipe through, prefetch.js abandons the
 * snapshot and asks the API instead — which applies the same filter server-side
 * over the whole catalogue and can widen into adjacent categories, something a
 * static snapshot has no way to do.
 */

/** The enriched taxonomy category, never the raw crawled `category` string. */
export const rowCategory = (item) => item?.attrs?.category || 'unknown';

/**
 * What the shopper would actually pay, or null when we don't really know.
 *
 * Zero is treated as absent, not free — the catalogue genuinely contains
 * zero-priced rows (bad crawls, made-to-order pieces), and because 0 satisfies
 * every "under ₹X" test they would otherwise be the first thing every
 * price-capped user sees. Mirrors search/builder.py's `effective_price`.
 */
export function effectivePrice(item) {
  for (const key of ['offer_price', 'price']) {
    const value = Number(item?.[key]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return null;
}

/** The preference shape this module works in, read off an /auth/me user. */
export function prefsFromUser(user) {
  return {
    categories: user?.discovery_taxonomy_categories || [],
    priceMax: user?.discovery_price_max ?? null,
    // Whether anything is actually being narrowed. Skipping the picker and
    // ticking every group are the same instruction — show me everything — and
    // the backend already decided which this is, so the client doesn't
    // re-derive it and risk disagreeing.
    active: Boolean(user?.discovery_prefs_active),
  };
}

/** A stable string for a set of preferences — the feed cache is keyed on it. */
export function prefsSignature(prefs) {
  if (!prefs?.active) return 'all';
  return `${[...(prefs.categories || [])].sort().join(',')}|${prefs.priceMax ?? ''}`;
}

/** Whether one product survives the filter. */
export function matchesPrefs(item, prefs) {
  if (!prefs?.active) return true;
  if (prefs.categories?.length && !prefs.categories.includes(rowCategory(item))) return false;
  if (prefs.priceMax != null) {
    const price = effectivePrice(item);
    if (price === null || price > prefs.priceMax) return false;
  }
  return true;
}

export function filterByPrefs(items, prefs) {
  if (!prefs?.active) return items;
  return items.filter((item) => matchesPrefs(item, prefs));
}
